var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { Table } from '@devexpress/dx-react-grid-material-ui';
import { ThemeProvider } from '@material-ui/styles';
import classNames from 'classnames';
import { debounce } from 'lodash';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';
import { PermissionsCheck } from '../Components/Auth/PermissionsCheck';
import { Button, ButtonMode } from '../Components/Button';
import { ImprovedDataTable } from '../Components/DataTable/ImprovedDataTable';
import { AddIcon, CloseIcon, ErrorCircleIcon, PrintIcon, StopHandIcon, TickIcon } from '../Components/Icons';
import { PharmacyRequestAllowedActions, PharmacyRequestStatus } from '../Contracts/PharmacyRequestModels';
import Pharmacist from '../Images/pharmacist.svg';
import { MedicationsPermissions } from '../MedicationsPermissions';
import { removeItemAtIndex } from '../Shared/ArrayUtils';
import { ValidDateFormats } from '../Shared/DateUtilities';
import { nameLastFirstTitle } from '../Shared/StringUtilities';
import { dangerVariant, iconLight, successVariant, white } from '../Styles/Colours';
import { muiMedicationsTheme } from '../Styles/Theme';
import { CancelPharmacyRequestDialog } from './CancelPharmacyRequestDialog';
import { StopPharmacyRequestDialog } from './StopPharmacyRequestDialog';
import styles from './PharmacyRequestsSummary.module.css';
import { PharmacyRequestsSummaryDetail } from './PharmacyRequestsSummaryDetail';
var getColumns = function (config) {
    var _a, _b;
    return [
        {
            name: 'requestNumber',
            title: ((_a = config.pharmacyRequestTerm) === null || _a === void 0 ? void 0 : _a.abbreviatedSingularTerm) + " number",
            getCellValue: function (row) {
                var _a;
                return (React.createElement("div", { className: styles.requestNumberText }, ((_a = config.pharmacyRequestTerm) === null || _a === void 0 ? void 0 : _a.singularTerm) + " #" + row.requestNumber));
            }
        },
        {
            name: 'encounterPlace',
            title: "" + ((_b = config.encounterPlaceTerm) === null || _b === void 0 ? void 0 : _b.singularTerm),
            getCellValue: function (row) { var _a; return (_a = row.encounterPlace) === null || _a === void 0 ? void 0 : _a.name; }
        },
        {
            name: 'createdBy',
            title: 'Created by',
            getCellValue: function (row) {
                var _a, _b, _c;
                return nameLastFirstTitle(((_a = row.requestedBy) === null || _a === void 0 ? void 0 : _a.familyName) || '', ((_b = row.requestedBy) === null || _b === void 0 ? void 0 : _b.givenName) || '', ((_c = row.requestedBy) === null || _c === void 0 ? void 0 : _c.title) || '');
            }
        },
        {
            name: 'createdOn',
            title: 'Created date',
            getCellValue: function (row) { return moment(row.startsOn).format(ValidDateFormats.DisplayWithHyphen); }
        },
        {
            name: 'expiresOn',
            title: 'End date',
            getCellValue: function (row) {
                return row.expiresOn === null ? '' : moment(row.expiresOn).format(ValidDateFormats.DisplayWithHyphen);
            }
        },
        {
            name: 'status',
            title: 'Status',
            getCellValue: function (row) {
                return React.createElement(PharmacyRequestStatusColumn, { expiresOn: row.expiresOn, status: row.status });
            }
        },
        {
            name: 'allowedActions',
            title: 'Actions',
            getCellValue: function (row) {
                return (React.createElement(AllowedActionButtons, { allowedActions: row.allowedActions, printPharmacyRequest: function () { return config.printPharmacyRequest(row); }, stopPharmacyRequest: function () { return config.stopPharmacyRequest(row); }, cancelPharmacyRequest: function () { return config.cancelPharmacyRequest(row); } }));
            }
        }
    ];
};
var PharmacyRequestStatusColumn = function (props) {
    var expiresOnMoment = moment(props.expiresOn, ValidDateFormats.IsoFormat);
    var expiryBeforeToday = !!props.expiresOn && expiresOnMoment.isValid() ? expiresOnMoment.isBefore(moment(), 'day') : false;
    if (props.status === PharmacyRequestStatus.Active && expiryBeforeToday) {
        return (React.createElement("div", { className: styles.statusHolder },
            React.createElement(ErrorCircleIcon, { size: '24', className: styles.statusIcon, color: dangerVariant }),
            React.createElement("span", { className: styles.danger }, "Expired")));
    }
    else if (props.status === PharmacyRequestStatus.Active) {
        return (React.createElement("div", { className: styles.statusHolder },
            React.createElement(TickIcon, { size: '24', className: styles.statusIcon, color: successVariant }),
            React.createElement("span", { className: styles.success }, "Active")));
    }
    else if (props.status === PharmacyRequestStatus.Stopped) {
        return (React.createElement("div", { className: styles.statusHolder },
            React.createElement(StopHandIcon, { size: '24', className: styles.statusIcon, color: iconLight }),
            React.createElement("span", { className: styles.stopped }, "Stopped")));
    }
    else if (props.status === PharmacyRequestStatus.Cancelled) {
        return (React.createElement("div", { className: styles.statusHolder },
            React.createElement(CloseIcon, { size: '24', className: styles.statusIcon, color: dangerVariant }),
            React.createElement("span", { className: styles.danger }, "Cancelled")));
    }
    else if (props.status === PharmacyRequestStatus.EnteredInError) {
        return (React.createElement("div", { className: styles.statusHolder },
            React.createElement("span", { className: styles.danger }, "Entered In Error")));
    }
    return React.createElement("div", null, props.status);
};
export var AllowedActionButtons = function (props) {
    if (!props.allowedActions || props.allowedActions.length === 0)
        return React.createElement(React.Fragment, null);
    var printButton = (React.createElement(Button, { onClick: function (e) {
            e.preventDefault();
            e.stopPropagation();
            props.printPharmacyRequest();
        }, iconOnly: true, noShadowOnHover: true, subtleShadeOnHover: true, mode: ButtonMode.text, tooltip: 'Print', testId: 'menuItem_Print' },
        React.createElement(PrintIcon, { color: iconLight, size: '24' })));
    var stopButton = (React.createElement(PermissionsCheck, { permission: MedicationsPermissions.WritePharmacyRequest },
        React.createElement(Button, { onClick: function (e) {
                e.preventDefault();
                e.stopPropagation();
                props.stopPharmacyRequest();
            }, iconOnly: true, noShadowOnHover: true, subtleShadeOnHover: true, mode: ButtonMode.text, tooltip: 'Stop', testId: 'menuItem_Stop' },
            React.createElement(StopHandIcon, { color: iconLight, size: '24' }))));
    var cancelButton = (React.createElement(PermissionsCheck, { permission: MedicationsPermissions.WritePharmacyRequest },
        React.createElement(Button, { onClick: function (e) {
                e.preventDefault();
                e.stopPropagation();
                props.cancelPharmacyRequest();
            }, iconOnly: true, noShadowOnHover: true, subtleShadeOnHover: true, mode: ButtonMode.text, tooltip: 'Cancel', testId: 'menuItem_Cancel' },
            React.createElement(CloseIcon, { color: iconLight, size: '24' }))));
    return (React.createElement("div", { className: styles.allowedActionButtons },
        props.allowedActions.includes(PharmacyRequestAllowedActions.Print) && printButton,
        props.allowedActions.includes(PharmacyRequestAllowedActions.Cancel) && cancelButton));
};
export var PharmacyRequestsSummary = function (props) {
    return (React.createElement(ThemeProvider, { theme: muiMedicationsTheme },
        React.createElement(Summary, __assign({}, props))));
};
var EncounterPlaceWidth = 300;
var CreatedByWidth = 300;
var CreatedOnWidth = 150;
var ExpiresOnWidth = 150;
var StatusWidth = 140;
var AllowedActionsWidth = 90;
var Summary = function (props) {
    var _a = __read(useState([]), 2), expandedItemIds = _a[0], setExpandedItemIds = _a[1];
    var _b = __read(useState(null), 2), pharmacyRequestToPrint = _b[0], setPharmacyRequestToPrint = _b[1];
    var _c = __read(useState(null), 2), pharmacyRequestToStop = _c[0], setPharmacyRequestToStop = _c[1];
    var _d = __read(useState(null), 2), pharmacyRequestToCancel = _d[0], setPharmacyRequestToCancel = _d[1];
    var _e = __read(useState(), 2), requestNumberColumnWidth = _e[0], setRequestNumberColumnWidth = _e[1];
    var updateRequestNumberWidth = function () {
        function reducer(previousValue, currentValue) {
            var _a, _b;
            if (currentValue.columnName !== 'requestNumber') {
                return previousValue + parseInt((_b = (_a = currentValue.width) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '0');
            }
            return 0;
        }
        var fixedColumnsWidth = columnExtensions.reduce(reducer, 0);
        var scrollbar = 32;
        var expander = 50;
        var filterWidth = props.filterPanelOpen ? 315 : 0;
        var screenWidth = window.innerWidth;
        var newWidth = screenWidth - fixedColumnsWidth - scrollbar - expander - filterWidth;
        var minWidth = 300;
        var width = Math.max(newWidth, minWidth);
        if (requestNumberColumnWidth !== width) {
            setRequestNumberColumnWidth(width);
        }
    };
    var requestNumberColumnWidthPicker = useCallback(debounce(function () {
        updateRequestNumberWidth();
    }, 300), []);
    useEffect(function () {
        updateRequestNumberWidth();
        window.addEventListener('resize', requestNumberColumnWidthPicker);
        return function () {
            window.removeEventListener('resize', requestNumberColumnWidthPicker);
        };
    }, []);
    useEffect(function () {
        updateRequestNumberWidth();
    }, [props.filterPanelOpen]);
    useEffect(function () {
        if (props.medicationNameFilter.length > 0 && props.pharmacyRequests.length !== 0)
            setExpandedItemIds(props.pharmacyRequests.map(function (x) { return x.id; }));
        else
            setExpandedItemIds([]);
    }, [props.pharmacyRequests]);
    var columnExtensions = [
        {
            columnName: 'requestNumber',
            width: requestNumberColumnWidth
        },
        {
            columnName: 'encounterPlace',
            width: EncounterPlaceWidth
        },
        {
            columnName: 'createdBy',
            width: CreatedByWidth
        },
        {
            columnName: 'createdOn',
            width: CreatedOnWidth
        },
        {
            columnName: 'expiresOn',
            width: ExpiresOnWidth
        },
        {
            columnName: 'status',
            width: StatusWidth
        },
        {
            columnName: 'allowedActions',
            width: AllowedActionsWidth
        }
    ];
    function StyledTableCell(props) {
        var _a;
        var cellStyles = classNames(styles.cell, (_a = {},
            _a[styles.interactive] = props.onClick !== undefined,
            _a[styles.expanded] = expandedItemIds.findIndex(function (it) { return it === props.row.id; }) >= 0,
            _a));
        var onClick = function (event) {
            if (props.onClick === undefined)
                return;
            if (props.row === undefined)
                return;
            event.preventDefault();
            event.stopPropagation();
            props.onClick(props.row);
        };
        return React.createElement(Table.Cell, __assign({}, props, { className: cellStyles, onClick: onClick }));
    }
    var CustomCellMemo = function (cellProps) {
        return React.createElement(StyledTableCell, __assign({}, cellProps));
    };
    var onRowExpand = function (row) {
        var index = expandedItemIds.findIndex(function (it) { return it === row.id; });
        if (index >= 0)
            setExpandedItemIds(removeItemAtIndex(expandedItemIds, index));
        else
            setExpandedItemIds(__spreadArray(__spreadArray([], __read(expandedItemIds), false), [row.id], false));
    };
    return (React.createElement(React.Fragment, null,
        props.pharmacyRequests === null || props.pharmacyRequests.length === 0 ? (React.createElement(PharmacyRequestsPlaceholder, { pharmacyRequestTerm: props.pharmacyRequestTerm, showAddPharmacyRequestWizard: props.showAddPharmacyRequestWizard })) : (React.createElement(ImprovedDataTable, { scrollMode: props.scrollMode, columns: getColumns({
                pharmacyRequestTerm: props.pharmacyRequestTerm,
                encounterPlaceTerm: props.encounterPlaceTerm,
                printPharmacyRequest: function (pharmacyRequest) {
                    setPharmacyRequestToPrint(pharmacyRequest);
                },
                stopPharmacyRequest: function (pharmacyRequest) {
                    setPharmacyRequestToStop(pharmacyRequest);
                },
                cancelPharmacyRequest: function (pharmacyRequest) {
                    setPharmacyRequestToCancel(pharmacyRequest);
                }
            }), data: props.pharmacyRequests, cellComponent: CustomCellMemo, getRowId: function (item) { return item.id; }, columnExtensions: columnExtensions, rowExpansion: {
                expandedIds: expandedItemIds,
                onExpand: onRowExpand,
                content: function (data) { return (React.createElement(PharmacyRequestsSummaryDetail, { row: data.row, columnWidths: {
                        card: (requestNumberColumnWidth !== null && requestNumberColumnWidth !== void 0 ? requestNumberColumnWidth : 0) + EncounterPlaceWidth,
                        orderInstructions: CreatedByWidth + CreatedOnWidth,
                        expires: ExpiresOnWidth
                    }, onHistoryClicked: function () { return props.showPharmacyRequestHistory(data.row); } })); },
                cellStyles: function () {
                    return styles.expandedSection;
                },
                columnWidth: 37,
                expanderOnLeft: true
            }, rowStyles: function (_a) {
                var _b;
                var expanded = _a.expanded;
                return classNames((_b = {}, _b[styles.expandedRow] = expanded, _b));
            }, sortingControl: [
                {
                    columnName: 'requestNumber',
                    sortingEnabled: true
                }
            ], noTableBorder: true, shortenContentByHeaderHeight: true, fillParentHeight: true })),
        pharmacyRequestToStop && (React.createElement(StopPharmacyRequestDialog, { patient: props.patient, pharmacyRequest: pharmacyRequestToStop, pharmacyRequestTerm: props.pharmacyRequestTerm, onStop: function () {
                props.onUpdatedPharmacyRequests();
                setPharmacyRequestToStop(null);
            }, onClose: function () {
                setPharmacyRequestToStop(null);
            } })),
        pharmacyRequestToCancel && (React.createElement(CancelPharmacyRequestDialog, { patient: props.patient, pharmacyRequest: pharmacyRequestToCancel, pharmacyRequestTerm: props.pharmacyRequestTerm, onCancel: function () {
                props.onUpdatedPharmacyRequests();
                setPharmacyRequestToCancel(null);
            }, onClose: function () {
                setPharmacyRequestToCancel(null);
            } }))));
};
var PharmacyRequestsPlaceholder = function (props) {
    var _a, _b, _c, _d;
    return (React.createElement("div", { className: styles.placeholder },
        React.createElement("div", { className: styles.placeholderContent },
            React.createElement("img", { src: Pharmacist, alt: '' }),
            React.createElement("div", { className: styles.placeholderText },
                "Add new ", (_b = (_a = props.pharmacyRequestTerm) === null || _a === void 0 ? void 0 : _a.singularTerm) === null || _b === void 0 ? void 0 :
                _b.toLowerCase(),
                " to your patient's record."),
            React.createElement(PermissionsCheck, { permission: MedicationsPermissions.WritePharmacyRequest },
                React.createElement(Button, { onClick: function () { return props.showAddPharmacyRequestWizard(); }, startIcon: React.createElement(AddIcon, { color: white }), testId: 'button-new-pharmacy-request' },
                    "Add ", (_d = (_c = props.pharmacyRequestTerm) === null || _c === void 0 ? void 0 : _c.singularTerm) === null || _d === void 0 ? void 0 :
                    _d.toLowerCase())))));
};
